import { useLocation, useNavigate } from 'react-router-dom';
import { useMemo, useLayoutEffect, useRef, type CSSProperties } from 'react';
import BottomNav from '@/components/feature/BottomNav';
import TabOrOutlet from '@/components/feature/TabOrOutlet';
import DesktopSidebar from '@/components/feature/DesktopSidebar';
import DesktopTopBar from '@/components/feature/DesktopTopBar';
import AdGate from '@/components/feature/AdGate';
import { useTheme } from '@/contexts/ThemeContext';
import { useHomeViewport } from '@/hooks/useHomeViewport';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { MotionConfig } from 'motion/react';
import '@/styles/study-workspace.css';

const tabPaths = ['/', '/history', '/ai-assistant', '/profile'];

function MobileLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const isScanFlow = location.pathname.startsWith('/scan') || location.pathname.startsWith('/video') || location.pathname.startsWith('/lesson');
  const isAI = location.pathname === '/ai-assistant';
  const isHome = location.pathname === '/';
  const homeViewport = useHomeViewport(true);
  const shellRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const shell = shellRef.current;
    const nav = shell?.querySelector<HTMLElement>('[data-bottom-navigation]');
    if (!shell || !nav) return;
    const measure = () => shell.style.setProperty('--home-nav-space', `${nav.getBoundingClientRect().height + 12}px`);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(nav);
    return () => observer.disconnect();
  }, [isScanFlow]);

  const isTabRoute = useMemo(
    () => tabPaths.includes(location.pathname),
    [location.pathname]
  );

  // AI page has its own scroll container; tab pages get default scrolling
  const mainOverflow = isAI || isHome ? 'overflow-hidden' : 'overflow-y-auto no-scrollbar overscroll-y-contain';
  // Only add bottom padding when nav is visible
  const showNav = !isScanFlow;
  const mainPaddingBottom = showNav && !isHome ? 'pb-[88px]' : '';

  return (
    <div className={`study-shell min-h-[100dvh] flex justify-center`} data-theme={resolvedTheme}>
      <div ref={shellRef} className={`w-full h-[100dvh] flex flex-col relative overflow-hidden`} data-home-viewport={isHome || undefined}
        style={{ position: 'fixed', top: 0, left: 0, height: homeViewport.height, '--home-baseline-height': `${homeViewport.height}px`, '--home-visible-height': `${homeViewport.visibleHeight}px`, '--home-overlay-bottom': homeViewport.keyboardOpen ? `${homeViewport.bottomInset + 8}px` : 'var(--home-nav-space, calc(82px + env(safe-area-inset-bottom, 0px)))' } as CSSProperties}>
        <main className={`workspace-content flex-1 min-h-0 flex flex-col ${mainOverflow} ${mainPaddingBottom}`}
          style={isAI && homeViewport.keyboardOpen ? { flex: '0 0 auto', height: homeViewport.visibleHeight } : undefined}>
          <TabOrOutlet isTabPath={isTabRoute} />
        </main>
        {!isScanFlow && (
          <BottomNav
            dark={isDark}
            isTabRoute={isTabRoute}
            currentPath={location.pathname}
            navigate={navigate}
          />
        )}
        <AdGate />
      </div>
    </div>
  );
}

function DesktopLayout() {
  const location = useLocation();
  const { resolvedTheme } = useTheme();

  const isTabRoute = useMemo(
    () => tabPaths.includes(location.pathname),
    [location.pathname]
  );

  return (
    <div className="study-shell min-h-[100dvh] h-[100dvh] overflow-hidden flex" data-theme={resolvedTheme}>
      <DesktopSidebar />
      <div className="flex-1 min-w-0 flex flex-col min-h-0 ml-[260px]">
        <DesktopTopBar />
        <main className="workspace-content flex-1 min-h-0 flex flex-col overflow-auto">
          <TabOrOutlet isTabPath={isTabRoute} />
        </main>
        <AdGate />
      </div>
    </div>
  );
}

export default function AppLayout() {
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  return <MotionConfig reducedMotion="user">{isDesktop ? <DesktopLayout /> : <MobileLayout />}</MotionConfig>;
}